import type MarkdownIt from 'markdown-it';

type Nesting = 1 | 0 | -1;

export type Token = {
	type: string;
	tag: string;
	nesting: Nesting;
	level: number;
	children: Token[] | null;
	content: string;
	block: boolean;
	attrs: [string, string][] | null;
};

export type StateCore = {
	tokens: Token[];
	Token: new (type: string, tag: string, nesting: Nesting) => Token;
};

const CITATION_REGEX = /\[\^(\d+)\^\]/;

/**
 * This plugin is replacing the default citation plugin to handle citations to allow
 * grouping consecutive citations into one citation node.
 * Copied from platform/packages/ai-mate/csm-chat-simulator/src/common/utils/content-transformer/md-tokenizer/md-plugin-citation/index.ts
 */
const groupedCitation = (state: StateCore) => {
	const tokens: Token[] = [];
	const citationIndexMap: Record<string, number> = {};
	let currentIndex = 1;

	for (const token of state.tokens) {
		const tokenChildren: Array<Token | null> = [];

		if (token.type !== 'inline' || (token.children && token?.children?.length <= 0)) {
			tokens.push(token);
			continue;
		}

		if (token.children) {
			token.children.forEach((childToken) => {
				if (childToken.type === 'text' && CITATION_REGEX.test(childToken.content)) {
					/**
					 * Example content: `Hello [^1^][^1^].`
					 * `parts` will be ['Hello ', '1', '', '1', '.']
					 */
					const parts = childToken.content.split(CITATION_REGEX);
					const newChildren: Token[] = [];

					/**
					 * we want to remove consecutive citation
					 * e.g. `Hello [^1^][^1^]. How are you [^1^][^2^]`
					 * should be transformed to `Hello 1. How are you 1 2`
					 */
					let lastCitation: string | null = null;
					let isLastCitationDuplicate = false;

					// only want to add space in the next part after citation
					// if the last citation is valid

					let groupedCitationsToken: Token = new state.Token('status', '', 0);
					let isGroupedCitationsTokenAdded = false;

					parts.forEach((part, index) => {
						if (index % 2 === 0) {
							// this is a text part

							/**
							 * if the last citation is a duplicate, and the text part is an empty string, we skip rendering the inbetween empty space
							 * So it doesn't end up rendering empty space that will end up with multi empty spaces
							 * because previous valid citation already added 1 empty space
							 *
							 * Example: For `[^1^][^1^]`
							 * it will be split into parts: ['', '1', '', '1', ''],
							 * This code will skip any rendering for the last empty string (index=4)
							 * Because the empty string at index=2 already added 1 empty space from the code below
							 */
							if (isLastCitationDuplicate && part.trim() === '') {
								return;
							}

							if (part.trim() !== '') {
								/**
								 * if the text part is not an empty string, we want to reset the last citation
								 * because the text part is a valid text, and not a space between citations
								 * so that we can render citation again, even if the next number is the same as the last number
								 *
								 * Example:
								 * `[^1^] [^1^] Hello [^1^][^1^]` -> `1 Hello 1`
								 */
								lastCitation = null;
								groupedCitationsToken = new state.Token('status', '', 0);
								isGroupedCitationsTokenAdded = false;
							}

							const textToken = new state.Token('text', '', 0);

							if (index === parts.length - 1) {
								textToken.content = part;
							} else if (part.trim() === '') {
								// Skip adding space for empty parts between consecutive citations
								return;
							} else {
								textToken.content = part + ' ';
							}
							textToken.block = false;
							textToken.level = childToken.level;
							newChildren.push(textToken);
						} else {
							// this is a citation part

							// if the current citation is the same as the last citation, we skip rendering the citation, and mark it as a duplicate
							if (part === lastCitation) {
								isLastCitationDuplicate = true;
								return;
							} else {
								// always reset the duplicate flag
								isLastCitationDuplicate = false;
								lastCitation = part;
							}

							groupedCitationsToken.block = false;
							groupedCitationsToken.level = childToken.level;

							const citationText = citationIndexMap[part]
								? citationIndexMap[part].toString()
								: currentIndex.toString();

							if (!citationIndexMap[part]) {
								citationIndexMap[part] = currentIndex;
								currentIndex++;
							}

							if (groupedCitationsToken.content) {
								groupedCitationsToken.content = groupedCitationsToken.content + ',' + citationText;
							} else {
								groupedCitationsToken.content = citationText;
							}

							groupedCitationsToken.attrs = [
								['color', 'blue'],
								// add the citations to attrs as a comma separated list
								['text', groupedCitationsToken.content],

								// localId is used to identify the citation token in the DOM
								// this overrides the uuid that is normally used
								['localId', `citation-${groupedCitationsToken.content}`],
							];

							if (!isGroupedCitationsTokenAdded) {
								newChildren.push(groupedCitationsToken);
								isGroupedCitationsTokenAdded = true;
							}
						}
					});

					tokenChildren.push(...newChildren);

					return;
				}

				tokenChildren.push(childToken);
			});
		}

		const filteredTokenChildren = tokenChildren.filter(
			(childToken) => childToken !== null,
		) as Token[];
		token.children = filteredTokenChildren;
		tokens.push(token);
	}

	state.tokens = tokens;
	return;
};

export default (md: MarkdownIt): void => md.core.ruler.at('citation', groupedCitation);
